import * as React from 'react'
import { preload } from 'react-dom'
import { useInView, useReducedMotion } from 'framer-motion'
import { tw } from 'utils/classnames'
import { Slot } from '@radix-ui/react-slot'

// --- Context ---

interface VideoFeatureCardContextValue {
  isInView: boolean
  isHovering: boolean
}

const VideoFeatureCardContext = React.createContext<VideoFeatureCardContextValue | null>(null)

function useVideoFeatureCardContext() {
  const context = React.useContext(VideoFeatureCardContext)
  if (!context) {
    throw new Error('VideoFeatureCard sub-components must be used within VideoFeatureCard.Root')
  }
  return context
}

// --- Sub-components ---

function Root({ children, className }: { children: React.ReactNode; className?: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef)
  const [isHovering, setIsHovering] = React.useState(false)

  const contextValue = React.useMemo(() => ({ isInView, isHovering }), [isInView, isHovering])

  return (
    <VideoFeatureCardContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={tw('w-full flex flex-col items-start group relative', className)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {children}
      </div>
    </VideoFeatureCardContext.Provider>
  )
}

function CardLink({
  asChild,
  className,
  children,
  ...rest
}: {
  asChild?: boolean
  className?: string
  children?: React.ReactNode
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const Component = asChild ? Slot : 'a'
  return (
    <Component className={tw('absolute inset-0 z-[2]', className)} {...rest}>
      {children}
    </Component>
  )
}

function Media({
  videoSrc,
  posterSrc,
  aspectRatio = '380/253',
  className,
  children,
  priority = false,
}: {
  videoSrc?: string
  posterSrc?: string
  aspectRatio?: string
  className?: string
  children?: React.ReactNode
  priority?: boolean
}) {
  const { isInView, isHovering } = useVideoFeatureCardContext()
  if (priority && posterSrc) {
    preload(posterSrc, { as: 'image', fetchPriority: 'high' })
  }
  const prefersReducedMotion = useReducedMotion()
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const hasBuiltInMedia = videoSrc && posterSrc

  React.useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (prefersReducedMotion) {
      if (isHovering) {
        void video.play().catch(() => {})
      } else {
        video.pause()
        video.currentTime = 0
      }
    }
  }, [prefersReducedMotion, isHovering])

  return (
    <div
      className={tw(
        'w-full rounded-4 overflow-hidden border border-theme_semantic_surface_border_color-regular relative flex items-center justify-center',
        className,
      )}
      style={{ aspectRatio }}
    >
      {hasBuiltInMedia ? (
        <>
          {isInView ? (
            <video
              ref={videoRef}
              src={videoSrc}
              poster={posterSrc}
              autoPlay={!prefersReducedMotion}
              loop
              muted
              playsInline
              className="absolute inset-0 size-full object-cover pointer-events-none"
            />
          ) : (
            <img
              src={posterSrc}
              alt=""
              className="absolute inset-0 size-full object-cover pointer-events-none"
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : undefined}
            />
          )}
        </>
      ) : null}
      {children}
    </div>
  )
}

function Overlay({ className }: { className?: string }) {
  return (
    <div
      className={tw(
        'absolute inset-[-1px] bg-theme_semantic_action_media_overlay-hover opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms]',
        className,
      )}
    />
  )
}

function HoverButton({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      tabIndex={-1}
      aria-hidden="true"
      className={tw(
        'relative -translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-[250ms] ease-out pointer-events-none',
        className,
      )}
    >
      {children}
    </div>
  )
}

function Title({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={tw(
        'studio-typography-16-bold text-theme_semantic_surface_ui_foreground-color-regular w-full',
        className,
      )}
    >
      {children}
    </span>
  )
}

function Description({
  children,
  className,
  variant = 'default',
}: {
  children: React.ReactNode
  className?: string
  variant?: 'default' | 'compact'
}) {
  return (
    <span
      className={tw(
        'truncate w-full',
        variant === 'default' &&
          'studio-typography-14 text-theme_semantic_surface_ui_foreground-color-on-media-faint',
        variant === 'compact' &&
          'studio-typography-12 text-theme_semantic_surface_ui_foreground-color-faint',
        className,
      )}
    >
      {children}
    </span>
  )
}

// --- Export ---

export { useVideoFeatureCardContext }

export const VideoFeatureCard = {
  Root,
  Link: CardLink,
  Media,
  Overlay,
  HoverButton,
  Title,
  Description,
}
